import type { PlannerProject } from "../types";
import type { PriceValues } from "./priceList";
import { syncRenderOrderStatus } from "./renderRoom";

export interface DesignerContact {
  name: string;
  phone: string;
  email: string;
  comment: string;
}

export interface DesignerVisualization {
  blob: Blob;
  fileName: string;
}

const safeFileName = (value: string) =>
  value
    .trim()
    .replace(/[<>:"/\\|?*\u0000-\u001F]/g, "-")
    .replace(/\s+/g, " ")
    .slice(0, 80) || "Проект";

/**
 * Короткий отпечаток содержимого проекта (FNV-1a): тот же проект — тот же
 * отпечаток, любое изменение изделия или комнаты — другой. Выделение в 3D не в счёт.
 */
export const projectOrderKey = (project: PlannerProject) => {
  const { selectedModuleId: _module, selectedItemId: _item, selectedShelvingCellId: _cell, ...content } = project;
  const text = JSON.stringify(content);
  let hash = 0x811c9dc5;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return `${hash.toString(16).padStart(8, "0")}${text.length.toString(16)}`;
};

export async function sendDesignerOrder(
  project: PlannerProject,
  screenshot: string,
  contact: DesignerContact,
  prices: PriceValues | null,
  visualization?: DesignerVisualization
) {
  // Страница открыта со старой версии, а конфигуратор уже обновили: старых
  // файлов PDF-модуля на сервере больше нет (10.10).
  const [{ createProjectPdfBlob }, { createAssemblyPdfBlob }] =
    await Promise.all([
      import("./exportPdf"),
      import("./assemblyPdf")
    ]).catch((error: unknown) => {
      console.error("Unable to load PDF modules", error);
      throw new Error("Конфигуратор обновился. Обновите страницу — проект сохранится — и отправьте заявку ещё раз.");
    });
  const [clientPdf, assemblyPdf] = await Promise.all([
    createProjectPdfBlob(project, screenshot, prices),
    createAssemblyPdfBlob(project, screenshot)
  ]);
  const projectName = safeFileName(project.name);
  const payload = new FormData();
  payload.append("name", contact.name);
  payload.append("phone", contact.phone);
  payload.append("email", contact.email);
  payload.append("comment", contact.comment);
  payload.append("projectName", project.name);
  // Номер и отпечаток проекта: по ним сервер отличает новую заявку от повторной отправки той же.
  payload.append("projectNumber", project.number ?? "");
  payload.append("projectKey", projectOrderKey(project));
  payload.append("clientPdf", clientPdf, `${projectName} — проект.pdf`);
  payload.append("assemblyPdf", assemblyPdf, `${projectName} — техническая карта.pdf`);
  if (visualization) {
    payload.append(
      "visualization",
      visualization.blob,
      safeFileName(visualization.fileName)
    );
  }

  const response = await fetch("/api/orders", {
    method: "POST",
    body: payload
  });
  const result = (await response.json().catch(() => null)) as
    | {
        ok?: boolean;
        error?: string;
        approvalStatus?: "pending" | "approved" | "rejected";
        renderStatusToken?: string;
        duplicate?: boolean;
        telegramDelivered?: boolean;
        requestNumber?: number | null;
      }
    | null;
  if (!response.ok || !result?.ok) {
    // Код ответа — в тексте ошибки: по нему видно, где застряла заявка (11.10).
    throw new Error(result?.error || (response.status === 504 || response.status === 502
      ? `Сервер не ответил вовремя (ошибка ${response.status}). Попробуйте ещё раз через минуту.`
      : `Не удалось отправить заявку дизайнеру (ошибка ${response.status}).`));
  }

  if (result.renderStatusToken) {
    try {
      await syncRenderOrderStatus(result.renderStatusToken);
    } catch (error) {
      console.error("Unable to synchronize render approval status", error);
    }
  }

  return {
    approvalStatus: result.approvalStatus ?? "pending",
    duplicate: Boolean(result.duplicate),
    telegramDelivered: result.telegramDelivered,
    number: typeof result.requestNumber === "number" ? result.requestNumber : null
  };
}
